import { html } from 'lit-element';
import { nothing } from 'lit-html';
import { ifNotNull } from '../../utils/directives';
import {
  KatalComponent,
  KatLitElement,
  booleanConverter,
  coerceBooleanClassProperties,
  nextUniqueId,
} from '../../shared/base';

/**
 * @component {kat-input} KatalInput A form field that allows the customer to enter text.
 * @guideline Do All input fields should have a label.
 * @guideline Do Allow for extra space for extra characters when localized into other languages.
 * @guideline Do Include brief hint (placeholder) text inside an input field if you need to indicate what the customer should type into the field, however leave the field blank if the label is self-explanatory.
 * @event blur This event is fired whenever the input is un-selected
 * @event input This event is fired whenever the user types a single character
 * @event change This event is fired whenever the user commits their change
 * @event focus This event is fired whenever the input is selected
 * @event keydown This event is fired whenever the keyboard is starts entering a character
 * @event keyup This event is fired whenever the keyboard is finished entering a character
 * @event mouseup This event is fired whenever the mouse button is released
 * @status Production
 * @theme flo
 * @example Basic {"value": "John Doe", "label":"Name", "type":"text"}
 * @example WithConstraint {"value": "John Doe", "label":"Name", "type":"text","constraint-emphasis":"Attention:", "constraint-label":"This is your first and last name"}
 * @example ErrorState {"value": "", "label":"Name", "type":"text","state-emphasis":"Error:", "state-label":"your name is required", "state": "error"}
 * @example Disabled {"value": "John Doe", "label":"Name", "type":"text", "disabled":"true"}
 * @example WithTooltip {
 *   "label": "Password",
 *   "type": "password",
 *   "tooltip-text": "Passwords must contain one of each of the following: letter, number, symbol"
 * }
 * @example WithCustomTooltip {
 *   "label": "Password",
 *   "type": "password",
 *   "tooltip-text": "Passwords must contain one of each of the following: letter, number, symbol",
 *   "tooltip-position": "right",
 *   "tooltip-trigger-icon": "warning"
 * }
 */
export class KatalInput extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {string} value This is the input provided by the user that can be edited
       */
      value: { type: String, reflect: true },
      /**
       * @classprop {enum} type The type of input to display to a user.
       * @enum {value} text Collects basic text input from a user.  This is the default option
       * @enum {value} password Allows a user to enter a password
       * @enum {value} number Allows a user to enter in a number only
       * @enum {value} email defines a field for an e-mail address
       * @enum {value} search defines a text field for entering a search string
       * @enum {value} url defines a field for entering a URL.
       */
      type: { type: String, reflect: true },
      /**
       * @classprop {string} label Label that describes what the input represents
       */
      label: { type: String, reflect: true },
      /**
       * @classprop {string} unique-id Used to associate an external label to an input.  See the kat-label for attribute.
       */
      uniqueId: { type: String, attribute: 'unique-id', reflect: true },
      /**
       * @classprop {string} placeholder Provides a example of what the user is suppose to enter
       */
      placeholder: { type: String, reflect: true },
      /**
       * @classprop {string} constraint-emphasis Optional pretext for the constraint label.  Used to provide addtional context to the constraint for the input
       */
      constraintEmphasis: {
        type: String,
        attribute: 'constraint-emphasis',
        reflect: true,
      },
      /**
       * @classprop {string} constraint-label Provides users with more information about what they enter into the input
       */
      constraintLabel: {
        type: String,
        attribute: 'constraint-label',
        reflect: true,
      },
      /**
       * @classprop {enum} state Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
       * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
       */
      state: { type: String, reflect: true },
      /**
       * @classprop {string} state-emphasis Optional pretext for the state label.  Used to provide addtional context to the state for the input
       */
      stateEmphasis: {
        type: String,
        attribute: 'state-emphasis',
        reflect: true,
      },
      /**
       * @classprop {string} state-label Provides users with more information about why the input is in the state its in
       */
      stateLabel: { type: String, attribute: 'state-label', reflect: true },
      /**
       * @classprop {string} step Specifies the intervals numbers can be entered
       */
      step: { type: String, reflect: true },
      /**
       * @classprop {boolean} disabled prevents the user from changing the value of an input, prevents events from being triggered, prevents value collection in a form
       */
      disabled: { type: Boolean, converter: booleanConverter, reflect: true },
      /**
       * @classprop {string} name Specifies the value of the input when it is sent to the server in a form submission
       */
      name: { type: String, reflect: true },
      /**
       * @classprop {string} pattern Specifies a regular expression that the value is checked against
       */
      pattern: { type: String, reflect: true },
      /**
       * @classprop {boolean} readonly Specifies that the element is read only
       */
      readonly: { type: Boolean, converter: booleanConverter, reflect: true },
      /**
       * @classprop {boolean} autofocus Specifies that the element should recieve focus by default
       */
      autofocus: { type: Boolean, converter: booleanConverter, reflect: true },
      /**
       * @classprop {boolean} required Specifies that the element should recieve focus by default
       */
      required: { type: Boolean, converter: booleanConverter, reflect: true },
      /**
       * @classprop {string | number} maxlength Specifies the maxlength
       */
      maxlength: { type: Number, reflect: true },
      /**
       * @classprop {string | number} min Specifies a minimum value
       */
      min: { type: Number, reflect: true },
      /**
       * @classprop {string | number} max Specifies a max value
       */
      max: { type: Number, reflect: true },
      /**
       * @classprop {string} autocomplete Specifies the autocomplete behaviour that a browser should attempt:
       * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
       */
      autocomplete: { type: String, reflect: true },
      /**
       * @classprop {string} tooltip-text This is the text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
       * set.
       */
      tooltipText: { type: String, attribute: 'tooltip-text', reflect: true },
      /**
       * @classprop {string} tooltip-position This is the position of the tooltip. Defaults to "top".
       */
      tooltipPosition: {
        type: String,
        attribute: 'tooltip-position',
        reflect: true,
      },
      /**
       * @classprop {string} tooltip-trigger-icon This is the icon that triggers the tooltip next to the label. Defaults to "help_outline".
       */
      tooltipTriggerIcon: {
        type: String,
        attribute: 'tooltip-trigger-icon',
        reflect: true,
      },
      /**
       * @classprop {string} kat-aria-label The label used exclusively by screenreaders as a fallback if the regular label is not specified.
       */
      katAriaLabel: {
        type: String,
        attribute: 'kat-aria-label',
        reflect: true,
      },
      /**
       * @classprop {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
       * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
       * more details.
       */
      katal12SpacingFixes: {
        type: Boolean,
        converter: booleanConverter,
        attribute: 'katal-1-2-spacing-fixes',
        reflect: true,
      },
    };
  }

  constructor() {
    super();

    this.uniqueId = nextUniqueId();
    this.type = 'text';
    this.tooltipPosition = 'top';
    this.tooltipTriggerIcon = 'help_outline';
  }

  _suppressedInputEventNames = [
    'blur',
    'change',
    'input',
    'keydown',
    'keyup',
    'click',
  ];

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  render() {
    const eventProxy = e => this.handleEvent(e);

    const label = this.label
      ? html`<kat-label
        class="form-label"
        for=${this.uniqueId}
        text=${this.label}
        tooltip-text=${ifNotNull(this.tooltipText)}
        tooltip-position=${ifNotNull(this.tooltipPosition)}
        tooltip-trigger-icon=${ifNotNull(
          this.tooltipTriggerIcon
        )}></<kat-label>`
      : nothing;

    const constraintLabel = html`
      <kat-label
        class="constraint-label"
        for=${this.uniqueId}
        variant="constraint"
        emphasis=${ifNotNull(this.constraintEmphasis)}
        text=${ifNotNull(this.constraintLabel)}
      ></kat-label>
    `;

    const stateLabel = html`
      <kat-label
        class="state-label"
        variant="constraint"
        text=${ifNotNull(this.stateLabel)}
        emphasis=${ifNotNull(this.stateEmphasis)}
        state=${ifNotNull(this.state)}
      ></kat-label>
    `;

    return html`
      ${label}
      <input
        id=${this.uniqueId}
        class=${ifNotNull(this.state)}
        type=${this.type}
        placeholder=${ifNotNull(this.placeholder)}
        name=${ifNotNull(this.name)}
        step=${ifNotNull(this.step)}
        maxlength=${ifNotNull(this.maxlength)}
        pattern=${ifNotNull(this.pattern)}
        min=${ifNotNull(this.min)}
        max=${ifNotNull(this.max)}
        autocomplete=${ifNotNull(this.autocomplete)}
        .value=${this.value ? this.value : ''}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
        ?autofocus=${this.autofocus}
        ?required=${this.required}
        @click=${eventProxy}
        @blur=${eventProxy}
        @focus=${eventProxy}
        @change=${eventProxy}
        @keydown=${eventProxy}
        @keyup=${eventProxy}
        @mouseup=${eventProxy}
        @input=${eventProxy}
      />
      ${constraintLabel} ${stateLabel}
    `;
  }

  handleEvent(event) {
    if (event.target.tagName !== 'INPUT') {
      return;
    }

    // Only prevent propagation of events we intend to capture. For example, Firefox depends on input's focus event to draw the cursor
    if (this._suppressedInputEventNames.includes(event.type)) {
      event.stopImmediatePropagation();
    }

    // Bubble up all bound events except change and exit here
    if (event.type !== 'change') {
      const newEvent = new CustomEvent(event.type, {
        bubbles: true,
        detail: { value: event.target.value },
      });

      if (event.key) {
        //Proxy keyboard events through if available
        newEvent.key = event.key;
      }
      if (event.keyCode) {
        //Proxy keyboard events through if available
        newEvent.keyCode = event.keyCode;
      }

      this.dispatchEvent(newEvent);
      return;
    }

    // For change events, only update top level component if the input actually differs from this component's "value" attribute
    const bubblingInputValue = event.target.value;
    const currentComponentValue = this.value;

    if (bubblingInputValue !== currentComponentValue) {
      const newEvent = new CustomEvent(event.type, {
        bubbles: true,
        detail: { value: event.target.value },
      });
      this.setAttribute('value', event.target.value);
      this.dispatchEvent(newEvent);
    }
  }

  focus() {
    const input = this.getElementsByTagName('input')[0];
    if (input) {
      input.focus();
    }
  }
}
coerceBooleanClassProperties(KatalInput);
KatalComponent.register('kat-input', KatalInput);
